import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTheme } from "next-themes";
import { cn, Icon } from "@project/ui";
import { authClient } from "@apps/dashboard/lib/auth-client";
import { BrandAuthHero } from "@apps/dashboard/components/brand/BrandAuthHero";
import { AuthCenterLayout } from "@apps/dashboard/components/auth/AuthCenterLayout";
import { AppleSignInButton } from "@apps/dashboard/components/auth/AppleSignInButton";
import { GoogleSignInButton } from "@apps/dashboard/components/auth/GoogleSignInButton";
import { PasskeyAuthButton } from "@apps/dashboard/components/auth/PasskeyAuthButton";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import {
  buildOAuthCallbackUrl,
  isAppleAuthEnabled,
  isGoogleAuthEnabled,
  isPasskeyAuthEnabled,
} from "@apps/dashboard/lib/env";
import { translateAuthError } from "@apps/dashboard/features/auth/map-auth-error";
import { tryAcceptStoredHouseInvite } from "@apps/dashboard/features/houses/accept-stored-house-invite";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import {
  getStoredHouseInviteToken,
  storeHouseInviteTokenFromSearchParams,
} from "@apps/dashboard/lib/house-invite-token";
import {
  shouldShowSocialProvider,
  useOAuthServerHints,
} from "@apps/dashboard/hooks/useOAuthServerHints";
import { trackDashboardEvent } from "@apps/dashboard/lib/analytics";

type LocationState = { from?: { pathname: string } };

export default function LoginPage(): JSX.Element {
  const { t, locale } = useI18n();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const from = (location.state as LocationState | null)?.from?.pathname ?? "/";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [googleSubmitting, setGoogleSubmitting] = useState(false);
  const [appleSubmitting, setAppleSubmitting] = useState(false);
  const [passkeySubmitting, setPasskeySubmitting] = useState(false);
  const [themeMounted, setThemeMounted] = useState(false);
  const oauthHints = useOAuthServerHints();
  const showGoogle = shouldShowSocialProvider(
    isGoogleAuthEnabled(),
    oauthHints.status,
    oauthHints.data,
    "google",
  );
  const showApple = shouldShowSocialProvider(
    isAppleAuthEnabled(),
    oauthHints.status,
    oauthHints.data,
    "apple",
  );
  const showPasskey = isPasskeyAuthEnabled();

  useEffect(() => {
    storeHouseInviteTokenFromSearchParams(searchParams);
  }, [searchParams]);

  useEffect(() => {
    setThemeMounted(true);
  }, []);

  const inviteToken = useMemo(() => {
    const raw = searchParams.get("invite")?.trim() ?? "";
    if (raw.length >= 32) {
      return raw;
    }
    return getStoredHouseInviteToken();
  }, [searchParams]);

  const hasPendingInvite = Boolean(inviteToken && inviteToken.length >= 32);

  const registerHref =
    hasPendingInvite && inviteToken
      ? `${dashboardPaths.register}?invite=${encodeURIComponent(inviteToken)}`
      : dashboardPaths.register;

  async function onSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    void trackDashboardEvent("login_submit", { method: "password", platform: "web" });
    setError(null);
    setSubmitting(true);
    try {
      const { error: err } = await authClient.signIn.email({
        email: email.trim(),
        password,
      });
      if (err) {
        void trackDashboardEvent("login_error", { method: "password", platform: "web" });
        setError(translateAuthError(err, t));
        return;
      }
      void trackDashboardEvent("login_success", { method: "password", platform: "web" });
      const outcome = await tryAcceptStoredHouseInvite(locale, navigate);
      if (outcome !== "navigated") {
        navigate(from, { replace: true });
      }
    } catch (unknownErr) {
      void trackDashboardEvent("login_error", { method: "password", platform: "web" });
      setError(translateAuthError(unknownErr, t));
    } finally {
      setSubmitting(false);
    }
  }

  async function onGoogleSignIn(): Promise<void> {
    void trackDashboardEvent("login_submit", { method: "google_oauth", platform: "web" });
    setError(null);
    setGoogleSubmitting(true);
    try {
      const { error: err } = await authClient.signIn.social({
        provider: "google",
        callbackURL: buildOAuthCallbackUrl(from),
      });
      if (err) {
        void trackDashboardEvent("login_error", { method: "google_oauth", platform: "web" });
        setError(translateAuthError(err, t));
        setGoogleSubmitting(false);
      }
    } catch (unknownErr) {
      void trackDashboardEvent("login_error", { method: "google_oauth", platform: "web" });
      setError(translateAuthError(unknownErr, t));
      setGoogleSubmitting(false);
    }
  }

  async function onAppleSignIn(): Promise<void> {
    void trackDashboardEvent("login_submit", { method: "apple_oauth", platform: "web" });
    setError(null);
    setAppleSubmitting(true);
    try {
      const { error: err } = await authClient.signIn.social({
        provider: "apple",
        callbackURL: buildOAuthCallbackUrl(from),
      });
      if (err) {
        void trackDashboardEvent("login_error", { method: "apple_oauth", platform: "web" });
        setError(translateAuthError(err, t));
        setAppleSubmitting(false);
      }
    } catch (unknownErr) {
      void trackDashboardEvent("login_error", { method: "apple_oauth", platform: "web" });
      setError(translateAuthError(unknownErr, t));
      setAppleSubmitting(false);
    }
  }

  async function onPasskeySignIn(): Promise<void> {
    void trackDashboardEvent("login_submit", { method: "passkey", platform: "web" });
    setError(null);
    setPasskeySubmitting(true);
    try {
      const result = await authClient.signIn.passkey({
        autoFill: true,
      });
      if (result.error) {
        void trackDashboardEvent("login_error", { method: "passkey", platform: "web" });
        setError(translateAuthError(result.error, t));
        return;
      }
      void trackDashboardEvent("login_success", { method: "passkey", platform: "web" });
      const outcome = await tryAcceptStoredHouseInvite(locale, navigate);
      if (outcome !== "navigated") {
        navigate(from, { replace: true });
      }
    } catch (unknownErr) {
      void trackDashboardEvent("login_error", { method: "passkey", platform: "web" });
      setError(translateAuthError(unknownErr, t));
    } finally {
      setPasskeySubmitting(false);
    }
  }

  return (
    <AuthCenterLayout>
      <div
        className="fixed top-4 right-4 z-50 flex items-center gap-1 rounded-lg border border-outline-variant/30 bg-surface-container-low/95 p-1 shadow-sm backdrop-blur-sm sm:top-6 sm:right-6"
        role="group"
        aria-label={t("common.theme.label")}
      >
        {(
          [
            { id: "light", icon: "Sun", label: t("common.theme.light") },
            { id: "dark", icon: "Moon", label: t("common.theme.dark") },
            { id: "system", icon: "Monitor", label: t("common.theme.system") },
          ] as const
        ).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTheme(item.id)}
            aria-label={item.label}
            title={item.label}
            className={cn(
              "rounded-md p-1.5 transition-colors",
              themeMounted && theme === item.id
                ? "bg-primary text-on-primary"
                : "text-on-surface-variant hover:bg-surface-container-high hover:text-primary",
            )}
          >
            <Icon name={item.icon} size={16} aria-hidden />
          </button>
        ))}
      </div>

      <div className="relative z-10 w-full max-w-[480px]">
        <BrandAuthHero className="mb-12" />

        <div className="rounded-xl border border-outline-variant/15 bg-surface-container-lowest p-10 shadow-sm">
          <div className="mb-10 text-center">
            <h2 className="mb-2 font-headline text-headline-sm font-bold text-primary">
              {t("dashboardUi.auth.loginTitle")}
            </h2>
            <p className="text-body-md text-on-surface-variant">{t("dashboardUi.auth.loginSubtitle")}</p>
          </div>

          {hasPendingInvite ? (
            <div
              className="mb-6 rounded-lg bg-primary/5 px-4 py-3 text-body-md text-on-surface outline outline-1 outline-primary/15"
              role="status"
            >
              {t("dashboardUi.auth.inviteLoginBanner")}
            </div>
          ) : null}

          {error ? (
            <div
              className="mb-6 rounded-lg bg-error/10 px-4 py-3 text-body-md text-error outline outline-1 outline-error/20"
              role="alert"
            >
              {error}
            </div>
          ) : null}

          {showPasskey || showApple || showGoogle ? (
            <div className="mb-6 space-y-4">
              {showPasskey ? (
                <div className="space-y-2">
                  <PasskeyAuthButton
                    label={t("dashboardUi.auth.continueWithPasskey")}
                    loadingLabel={t("dashboardUi.auth.continuingWithPasskey")}
                    loading={passkeySubmitting}
                    disabled={submitting || googleSubmitting || appleSubmitting}
                    onClick={onPasskeySignIn}
                  />
                  <p className="text-center text-[11px] leading-snug text-on-surface-variant/90">
                    {t("dashboardUi.auth.passkeyPreferredHint")}
                  </p>
                </div>
              ) : null}
              {showApple ? (
                <AppleSignInButton
                  label={t("dashboardUi.auth.continueWithApple")}
                  loadingLabel={t("dashboardUi.auth.continuingWithApple")}
                  loading={appleSubmitting}
                  disabled={submitting || googleSubmitting || passkeySubmitting}
                  onClick={onAppleSignIn}
                />
              ) : null}
              {showGoogle ? (
                <GoogleSignInButton
                  label={t("dashboardUi.auth.continueWithGoogle")}
                  loadingLabel={t("dashboardUi.auth.continuingWithGoogle")}
                  loading={googleSubmitting}
                  disabled={submitting || appleSubmitting || passkeySubmitting}
                  onClick={onGoogleSignIn}
                />
              ) : null}
              <div className="relative flex items-center justify-center">
                <span className="absolute w-full border-t border-outline-variant/20" aria-hidden />
                <span className="relative bg-surface-container-lowest px-3 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                  {t("dashboardUi.auth.dividerOr")}
                </span>
              </div>
            </div>
          ) : null}

          <form className="space-y-6" onSubmit={onSubmit}>
            <div className="space-y-1.5">
              <label
                htmlFor="login-email"
                className="ml-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant"
              >
                {t("dashboardUi.auth.emailLabel")}
              </label>
              <input
                id="login-email"
                name="email"
                type="email"
                autoComplete="username webauthn"
                required
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder={t("dashboardUi.auth.emailPlaceholder")}
                className="h-12 w-full rounded-lg border-none bg-surface-container-low px-4 text-on-surface outline-none transition-all duration-200 placeholder:text-on-surface-variant/60 focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary"
              />
            </div>
            <div className="space-y-1.5">
              <div className="ml-1 flex items-end justify-between">
                <label
                  htmlFor="login-password"
                  className="text-[10px] font-bold uppercase tracking-wider text-on-surface-variant"
                >
                  {t("dashboardUi.auth.passwordLabel")}
                </label>
                <Link
                  to={dashboardPaths.forgotPassword}
                  className="text-[10px] font-bold uppercase tracking-wider text-primary transition-colors hover:text-primary-container"
                >
                  {t("dashboardUi.auth.forgotLink")}
                </Link>
              </div>
              <input
                id="login-password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
                placeholder={t("dashboardUi.auth.passwordPlaceholder")}
                className="h-12 w-full rounded-lg border-none bg-surface-container-low px-4 text-on-surface outline-none transition-all duration-200 placeholder:text-on-surface-variant/60 focus:bg-surface-container-lowest focus:ring-1 focus:ring-primary"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="primary-gradient mt-4 h-12 w-full rounded-lg font-headline font-semibold tracking-tight text-on-primary shadow-md transition-all hover:shadow-lg active:scale-[0.98] disabled:opacity-60"
            >
              {submitting ? t("dashboardUi.auth.signingIn") : t("dashboardUi.auth.signInCta")}
            </button>
          </form>

          <p className="mt-10 text-center text-body-md text-on-surface-variant">
            {t("dashboardUi.auth.registerPrompt")}{" "}
            <Link
              to={registerHref}
              className="font-semibold text-primary underline decoration-primary/30 underline-offset-4 hover:underline"
            >
              {t("dashboardUi.auth.createAccountLink")}
            </Link>
          </p>
        </div>
      </div>
    </AuthCenterLayout>
  );
}
